/**
 * CTA defaults — single source of truth for prefab cards and resolver logic.
 *
 * Used by:
 *  - Component render() functions (Hero, CTA, Navigation) for runtime fallback
 *  - Business settings UI for the card picker
 *  - Onboarding wizard for the same picker
 *
 * Backend has a mirror file at apps/api/src/modules/site-generation/cta-defaults.ts
 * (DEFAULT_CTA_LABEL + resolveCtaLabel/resolveCtaOfferLine signatures match).
 * Two copies of a small constant is fine; sharing a package across API + components
 * isn't worth the build complexity for this.
 */
import { phoneHref } from "@servicepro/utils";

const DEFAULT_CTA_LABEL = "Call Now",
  DEFAULT_CTA_OFFER_LINE: string | null = null,
  CTA_CARDS: CtaCard[] = [
    {
      cardLabel: "Call Now",
      description: "Phone-first. No promotional claim.",
      label: "Call Now",
      offerLine: null,
      posture: "phone",
      seed: "call_now",
    },
    {
      cardLabel: "Schedule an Estimate",
      description: "Form-first. No promotional claim.",
      label: "Schedule an Estimate",
      offerLine: "By Appointment",
      posture: "form",
      seed: "schedule",
    },
    {
      cardLabel: "Free Quote",
      description: "Form-first. Promotes free estimates.",
      label: "Get a Free Quote",
      offerLine: "Free Estimates",
      posture: "form",
      seed: "free_quote",
    },
  ],
  /** Resolve the effective CTA label: owner-set value, or the system default. */
  resolveCtaLabel = (business: BusinessCtaShape | undefined): string => {
    const v = business?.ctaPrimaryLabel?.trim();
    if (v && v.length > 0) {
      return v;
    }
    return DEFAULT_CTA_LABEL;
  },
  /** Resolve the effective offer line. Null means "no offer chip should render". */
  resolveCtaOfferLine = (business: BusinessCtaShape | undefined): string | null => {
    const v = business?.ctaOfferLine?.trim();
    if (v && v.length > 0) {
      return v;
    }
    return DEFAULT_CTA_OFFER_LINE;
  },
  /**
   * Should the primary CTA URL default to a phone link?
   * True when the label starts with "Call" (case-insensitive). Components apply this
   * only when the owner hasn't set an explicit ctaUrl prop.
   */
  /** Neutral secondary label when the primary already calls. Deliberately not a
   *  borrowed offer ("Get a Free Quote") — the owner never agreed to one. */
  SECONDARY_FORM_CTA_TEXT = "Contact Us",
  CONTACT_PAGE_PATH = "/contact",
  /** The contact section's id on a site whose pages are one page. */
  CONTACT_SECTION_ANCHOR = "#contact",
  /**
   * A site path without its query, hash or trailing slash.
   * @param {string} path The path.
   * @returns {string} The bare path, "/" for the home page.
   */
  barePath = (path: string): string => {
    const [bare] = path.split(/[?#]/u),
      trimmed = bare.replace(/\/+$/u, "");
    if (trimmed === "") {
      return "/";
    }
    return trimmed;
  },
  /**
   * Whether the site is one page, so its contact buttons scroll to the contact
   * section rather than open a contact page. A published site's build says so;
   * an editor preview, which is not told, is judged by its page list.
   * @param {readonly string[]} [pagePaths] Every page the site has; unknown in an editor preview.
   * @param {boolean} [onePage] Whether the site is one page, from its build; unknown in an editor preview.
   * @returns {boolean} True for a one-page site, or a preview whose known page list has no contact page.
   */
  isOnePageSite = (pagePaths?: readonly string[], onePage?: boolean): boolean => onePage ?? (pagePaths !== undefined &&
    !pagePaths.some((pagePath) => barePath(pagePath) === CONTACT_PAGE_PATH)),
  /**
   * Whether a saved button url means "contact us": blank, the in-page anchor, or the contact page.
   * @param {string} url The saved url.
   * @returns {boolean} True for a contact link.
   */
  isContactLink = (url: string): boolean => {
    const saved = url.trim();
    return saved === "" || saved === CONTACT_SECTION_ANCHOR ||
      (saved.startsWith("/") && barePath(saved) === CONTACT_PAGE_PATH);
  },
  /**
   * Where a contact button goes. The contact page on a site that has one, which
   * is every multi-page site. A one-page site has no contact page, so every
   * contact button scrolls to the contact section instead. The legacy in-page
   * anchor "#contact" is still sent to the page where one exists: older pages
   * carry it with no contact section to land on. Stored values that are not a
   * contact link are respected.
   * @param {unknown} [url] The saved button url, a generator default.
   * @param {readonly string[]} [pagePaths] Every page the site has; unknown in an editor preview.
   * @param {boolean} [onePage] Whether the site is one page, from its build (see isOnePageSite).
   * @returns {string} Where the button goes.
   */
  contactUrl = (url?: unknown, pagePaths?: readonly string[], onePage?: boolean): string => {
    const saved = typeof url === "string" ? url : "";
    if (!isContactLink(saved)) {
      return saved;
    }
    if (isOnePageSite(pagePaths, onePage)) {
      return CONTACT_SECTION_ANCHOR;
    }
    const trimmed = saved.trim();
    if (trimmed === "" || trimmed === CONTACT_SECTION_ANCHOR) {
      return CONTACT_PAGE_PATH;
    }
    return saved;
  };
const resolvesToPhoneUrl = (label: unknown): boolean => {
    // In the Puck editor, fields with `contentEditable: true` are wrapped in an
    // <InlineTextField> React element before reaching the component's render() —
    // So callers (Hero/CTA/Navigation) may pass us a non-string value. Treat it
    // As "no phone bias"; the rendered text still works, the link just defaults
    // To "#contact" in editor preview when we can't inspect the underlying string.
    if (typeof label !== "string") {
      return false;
    }
    return /^call\b/i.test(label.trim());
  },
  /**
   * Which action the primary CTA performs.
   *
   * The card the owner picked in Business settings is the answer when there is
   * one — `ctaPresetSeed` records it. The label heuristic is only a fallback, for
   * businesses that predate the picker or typed their own label; it reads
   * "Call ..." as a phone CTA and everything else as a form.
   *
   * Callers use this to make the two hero buttons offer two different actions,
   * rather than a "Call Now" beside a "Call Us: (555) 123-4567".
   */
  ctaPosture = (
    business: { ctaPresetSeed?: string | null } | undefined,
    resolvedLabel: unknown,
  ): "phone" | "form" => {
    const card = CTA_CARDS.find((c) => c.seed === business?.ctaPresetSeed);
    if (card) {
      return card.posture;
    }
    if (resolvesToPhoneUrl(resolvedLabel)) {
      return "phone";
    }
    return "form";
  },
  /**
   * `url`, unless it is the page it is on: a button that links to its own page goes nowhere.
   * Without a page path (an editor preview) every link is kept.
   * @param {string | undefined} url Where the button goes.
   * @param {string} [pagePath] The page the button is on.
   * @returns {string | undefined} The url, or undefined for no button.
   */
  offPage = (url: string | undefined, pagePath?: string): string | undefined => {
    if (url === undefined || pagePath === undefined || !url.startsWith("/")) {
      return url;
    }
    if (barePath(url) === barePath(pagePath)) {
      return undefined;
    }
    return url;
  },
  /**
   * `url` as it works from the page it is on. A one-page site's sections are
   * on its home page, so from any other page "#contact" is "/#contact". A site
   * with pages keeps its in-page links, and so does an editor preview, which
   * has no page path.
   * @param {string | undefined} url Where the button goes.
   * @param {string | undefined} pagePath The page the button is on.
   * @param {boolean} onePage Whether the site is one page (see isOnePageSite).
   * @returns {string | undefined} The url, reaching the home page's section from another page.
   */
  fromPage = (url: string | undefined, pagePath: string | undefined, onePage: boolean): string | undefined => {
    if (!onePage || url?.startsWith("#") !== true || pagePath === undefined || barePath(pagePath) === "/") {
      return url;
    }
    return `/${url}`;
  },
  /**
   * The second button when the first already calls: the contact page, left out on the contact page.
   * @param {string} [pagePath] The page the buttons are on.
   * @param {readonly string[]} [pagePaths] Every page the site has; a one-page site gets its contact section.
   * @param {boolean} [onePage] Whether the site is one page, from its build (see isOnePageSite).
   * @returns {{ text: string; url: string } | undefined} The button, or undefined for none.
   */
  contactSecondary = (pagePath?: string, pagePaths?: readonly string[], onePage?: boolean): { text: string; url: string } | undefined => {
    const url = fromPage(offPage(contactUrl(CONTACT_PAGE_PATH, pagePaths, onePage), pagePath), pagePath, isOnePageSite(pagePaths, onePage));
    if (url === undefined) {
      return undefined;
    }
    return { text: SECONDARY_FORM_CTA_TEXT, url };
  },
  /**
   * The primary button. A phone-first CTA calls when there is a phone; without one it is
   * "Contact Us" to the contact page, never a "Call Now" that opens a form. No url means no button.
   * @param {object} cta What the button is made from.
   * @param {string} cta.text The resolved CTA label.
   * @param {boolean} cta.phoneFirst Whether the owner's CTA card puts the phone first.
   * @param {string | null | undefined} cta.phone The business phone.
   * @param {unknown} [cta.savedUrl] The saved ctaUrl prop, a generator default.
   * @param {string} [cta.pagePath] The page the button is on.
   * @param {readonly string[]} [cta.pagePaths] Every page the site has; a one-page site gets its contact section.
   * @param {boolean} [cta.onePage] Whether the site is one page, from its build (see isOnePageSite).
   * @returns {{ text: string; url: string | undefined; calls: boolean }} The button.
   */
  resolvePrimaryCta = ({
    text,
    phoneFirst,
    phone,
    savedUrl,
    pagePath,
    pagePaths,
    onePage,
  }: {
    text: string;
    phoneFirst: boolean;
    phone: string | null | undefined;
    savedUrl?: unknown;
    pagePath?: string;
    pagePaths?: readonly string[];
    onePage?: boolean;
  }): { text: string; url: string | undefined; calls: boolean } => {
    const hasPhone = phone !== null && phone !== undefined && phone !== "",
      contact = fromPage(offPage(contactUrl(savedUrl, pagePaths, onePage), pagePath), pagePath, isOnePageSite(pagePaths, onePage));
    if (phoneFirst && hasPhone) {
      return { calls: true, text, url: phoneHref(phone) };
    }
    if (phoneFirst) {
      return { calls: false, text: SECONDARY_FORM_CTA_TEXT, url: contact };
    }
    return { calls: false, text, url: contact };
  };

interface CtaCard {
  /** Telemetry seed — recorded as Business.ctaPresetSeed when the card is clicked. */
  seed: "call_now" | "schedule" | "free_quote";
  /** Card title shown to the owner. */
  cardLabel: string;
  /** One-line helper text on the card. */
  description: string;
  /** Value written to Business.ctaPrimaryLabel. */
  label: string;
  /** Value written to Business.ctaOfferLine. Null means "leave blank". */
  offerLine: string | null;
  /**
   * Which action this card puts first. Read as data rather than sniffed from
   * the label, so a card named "Talk to us" is not silently treated as a form.
   */
  posture: "phone" | "form";
}

interface BusinessCtaShape {
  ctaPrimaryLabel?: string | null;
  ctaOfferLine?: string | null;
}

export type { CtaCard };
export {
  DEFAULT_CTA_LABEL,
  DEFAULT_CTA_OFFER_LINE,
  CTA_CARDS,
  resolveCtaLabel,
  resolveCtaOfferLine,
  SECONDARY_FORM_CTA_TEXT,
  contactUrl,
  resolvesToPhoneUrl,
  ctaPosture,
  contactSecondary,
  isOnePageSite,
  offPage,
  resolvePrimaryCta,
};
